import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  Space,
  Text,
  Table,
  ActionIcon,
  Breadcrumbs,
  Anchor,
  Group,
  Select,
  useMantineTheme,
  Loader,
  Alert,
} from "@mantine/core";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import useSegmentHistoryApiService from "../services/api/SegmentHistoryApiService";
import ExportSegmentHistoryButton from "./ExportSegmentHistoryButton";

type SegmentHistoryContentProps = {
  onBack: () => void;
};

const SegmentHistoryContent: React.FC<SegmentHistoryContentProps> = ({
  onBack,
}) => {
  const theme = useMantineTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params with fallbacks
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialRowsPerPage = searchParams.get("rowsPerPage") || "10";

  const [page, setPageState] = useState(
    Number.isNaN(initialPage) || initialPage < 1 ? 1 : initialPage
  );
  const [rowsPerPage, setRowsPerPageState] = useState(
    ["10", "25", "50"].includes(initialRowsPerPage) ? initialRowsPerPage : "10"
  );

  // Sync URL params when state changes
  const updateSearchParams = useCallback(
    (newPage: number, newRowsPerPage: string) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", String(newPage));
      params.set("rowsPerPage", newRowsPerPage);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setPage = useCallback(
    (newPage: number) => {
      setPageState(newPage);
      updateSearchParams(newPage, rowsPerPage);
    },
    [rowsPerPage, updateSearchParams]
  );

  const setRowsPerPage = useCallback(
    (newRowsPerPage: string) => {
      setRowsPerPageState(newRowsPerPage);
      setPageState(1);
      updateSearchParams(1, newRowsPerPage);
    },
    [updateSearchParams]
  );

  // Sync state from URL on navigation (back/forward)
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    const urlRowsPerPage = searchParams.get("rowsPerPage") || "10";
    const validPage = Number.isNaN(urlPage) || urlPage < 1 ? 1 : urlPage;
    const validRowsPerPage = ["10", "25", "50"].includes(urlRowsPerPage)
      ? urlRowsPerPage
      : "10";

    if (validPage !== page) {
      setPageState(validPage);
    }
    if (validRowsPerPage !== rowsPerPage) {
      setRowsPerPageState(validRowsPerPage);
    }
  }, [searchParams, page, rowsPerPage]);

  const pageSize = parseInt(rowsPerPage, 10);
  const { data, isLoading, isError } = useSegmentHistoryApiService({
    page,
    page_size: pageSize,
  });

  const breadcrumbItems = [
    { title: "Segmentation", onClick: onBack },
    { title: "History Update Log", onClick: undefined },
  ].map((item, index, arr) => (
    <Anchor
      key={item.title}
      onClick={(e) => {
        e.preventDefault();
        if (item.onClick) {
          item.onClick();
        }
      }}
      size="sm"
      style={{
        color: theme.black,
        opacity: index === arr.length - 1 ? 1 : 0.6,
        cursor: index === arr.length - 1 ? "default" : "pointer",
        textDecoration: "none",
      }}
    >
      {item.title}
    </Anchor>
  ));

  const formatTimestamp = (timestamp: string): string =>
    dayjs(timestamp).format("YYYY-MM-DD HH:mm");

  if (isLoading) {
    return (
      <>
        <Space h="md" />
        <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
        <Space h="xl" />
        <div
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <Loader />
        </div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Space h="md" />
        <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
        <Space h="xl" />
        <Alert icon={<AlertCircle size={16} />} title="Error" color="red">
          Failed to load segment history. Please try again later.
        </Alert>
      </>
    );
  }

  const results = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = data?.pages || 1;

  // Calculate display range
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  return (
    <>
      <Space h="md" />
      <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
      <Space h="xl" />

      <Group position="apart" mb="md">
        <Text weight={600} size="lg">
          History Update Log
        </Text>
        <ExportSegmentHistoryButton />
      </Group>

      {results.length === 0 ? (
        <Text color="dimmed" size="sm" align="center" py="xl">
          No history entries found.
        </Text>
      ) : (
        <Table verticalSpacing="md">
          <thead>
            <tr>
              <th>
                <Text weight={600} size="sm">
                  Timestamp
                </Text>
              </th>
              <th>
                <Text weight={600} size="sm">
                  User
                </Text>
              </th>
              <th>
                <Text weight={600} size="sm">
                  Segment name
                </Text>
              </th>
              <th>
                <Text weight={600} size="sm">
                  Action
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((entry) => (
              <tr key={entry.id}>
                <td>
                  <Text size="sm" color="dimmed">
                    {formatTimestamp(entry.timestamp)}
                  </Text>
                </td>
                <td>
                  <Text size="sm">{entry.actor_name}</Text>
                </td>
                <td>
                  <Text size="sm">{entry.segment_name}</Text>
                </td>
                <td>
                  <Text size="sm">{entry.action_label}</Text>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Space h="md" />
      <Group position="right" spacing="md">
        <Group spacing="xs">
          <Text size="sm" color="dimmed">
            Rows per page:
          </Text>
          <Select
            value={rowsPerPage}
            onChange={(val) => {
              setRowsPerPage(val || "10");
            }}
            data={[
              { value: "10", label: "10" },
              { value: "25", label: "25" },
              { value: "50", label: "50" },
            ]}
            size="xs"
            style={{ width: 70 }}
          />
        </Group>
        <Text size="sm" color="dimmed">
          {startItem}-{endItem} of {totalCount}
        </Text>
        <Group spacing="xs">
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            <ChevronsLeft size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            <ChevronLeft size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <ChevronRight size={14} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
          >
            <ChevronsRight size={14} />
          </ActionIcon>
        </Group>
      </Group>
    </>
  );
};

export default SegmentHistoryContent;
